<template>
  <div class="admin-assignments">
    <div class="header">
      <h1>👥 生徒 ↔ 講師割当管理</h1>
      <p class="subtitle">バディシステム用の生徒と講師の割当を管理します</p>
    </div>

    <!-- フィルター・検索 -->
    <div class="filters-section">
      <div class="filter-row">
        <div class="search-input">
          <label>🔍 生徒名検索</label>
          <input 
            v-model="searchQuery" 
            type="text" 
            placeholder="例: 田中太郎" 
            class="form-input"
          />
        </div>

        <div class="instructor-filter">
          <label>👨‍🏫 担当講師</label>
          <select v-model="selectedInstructorId" class="form-select">
            <option value="all">全講師</option>
            <option v-for="i in instructors" :key="i.id" :value="i.id">
              {{ i.full_name }} ({{ i.email }})
            </option>
          </select>
        </div>
        
        <div class="filter-toggle">
          <label class="checkbox-label">
            <input 
              type="checkbox" 
              v-model="onlyUnassigned" 
              :disabled="selectedInstructorId !== 'all'"
              @change="resetAndReload"
            />
            <span :class="{ 'disabled-label': selectedInstructorId !== 'all' }">
              未割当のみ表示
            </span>
          </label>
        </div>

        <div class="page-size-selector">
          <label>📄 表示件数</label>
          <select v-model.number="pageSize" @change="resetAndReload" class="form-select">
            <option :value="10">10件</option>
            <option :value="20">20件</option>
            <option :value="50">50件</option>
            <option :value="100">100件</option>
          </select>
        </div>
      </div>
    </div>

    <!-- 一括操作バー -->
    <div v-if="selectedInstructorId !== 'all'" class="bulk-bar">
      <span class="bulk-info">選択: {{ selectedIds.length }}件</span>
      <button 
        class="btn btn-danger" 
        :disabled="selectedIds.length === 0 || isSaving" 
        @click="bulkUnassign"
      >
        割当解除（未割当へ）
      </button>
    </div>

    <!-- 統計情報 -->
    <div class="stats-section">
      <div class="stat-card">
        <div class="stat-number">{{ totalCount }}</div>
        <div class="stat-label">{{ onlyUnassigned ? '未割当生徒' : '全生徒' }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-number">{{ instructors.length }}</div>
        <div class="stat-label">登録講師</div>
      </div>
      <div class="stat-card">
        <div class="stat-number">{{ groups.length }}</div>
        <div class="stat-label">グループ数</div>
      </div>
    </div>

    <!-- データテーブル -->
    <div class="table-section">
      <div v-if="isLoading" class="loading">
        <div class="spinner"></div>
        <p>データを読み込み中...</p>
      </div>

      <div v-else-if="rows.length === 0" class="no-data">
        <p>{{ onlyUnassigned ? '未割当の生徒はいません' : '該当する生徒がいません' }}</p>
      </div>

      <div v-else class="table-container">
        <table class="assignments-table">
          <thead>
            <tr>
              <th class="col-select">
                <input type="checkbox" v-model="allChecked" @change="toggleAll" />
              </th>
              <th class="col-index">#</th>
              <th class="col-student">👤 生徒</th>
              <th class="col-instructor">👨‍🏫 担任講師</th>
              <th class="col-group">👥 グループ</th>
              <th class="col-note">📝 備考</th>
              <th class="col-actions">⚙️ 操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in rows" :key="row.learner_user_id" class="table-row">
              <td class="col-select">
                <input type="checkbox" v-model="selectedIds" :value="row.learner_user_id" />
              </td>
              <td class="col-index">{{ (page - 1) * pageSize + index + 1 }}</td>
              
              <td class="col-student">
                <div class="student-info" :class="{ 'row-assigned': hasAssignment(row) }">
                  <div class="student-name">{{ row.display_name || '名前未設定' }}</div>
                  <div class="assignment-badge"
                       :class="hasAssignment(row) ? 'assigned' : 'unassigned'">
                    <template v-if="hasAssignment(row)">
                      担当：{{ instructorMap[row.primary_instructor_id]?.full_name || '不明' }}
                    </template>
                    <template v-else>
                      未割当
                    </template>
                  </div>
                  <div v-if="row.name_kana" class="student-kana">{{ row.name_kana }}</div>
                  <div class="student-id" v-if="row.learner_user_id">
                    ID: {{ row.learner_user_id.slice(0, 8) }}...
                  </div>
                </div>
              </td>
              
              <td class="col-instructor">
                <select 
                  v-model="row.primary_instructor_id" 
                  class="form-select instructor-select"
                  :class="{ 'required': !row.primary_instructor_id }"
                >
                  <option :value="null" disabled>
                    {{ hasAssignment(row) ? '（未割当にする）' : '講師を選択してください' }}
                  </option>
                  <option 
                    v-for="instructor in instructors" 
                    :key="instructor.id" 
                    :value="instructor.id"
                  >
                    {{ instructor.full_name }} ({{ instructor.email }})
                  </option>
                </select>
              </td>
              
              <td class="col-group">
                <select v-model="row.group_id" class="form-select group-select">
                  <option :value="null">未設定</option>
                  <option 
                    v-for="group in groups" 
                    :key="group.id" 
                    :value="group.id"
                  >
                    {{ group.name }}
                  </option>
                </select>
              </td>
              
              <td class="col-note">
                <input 
                  v-model="row.note" 
                  type="text" 
                  placeholder="備考（任意）" 
                  class="form-input note-input"
                />
              </td>
              
              <td class="col-actions">
                <div class="action-buttons">
                  <button
                    class="btn btn-save"
                    :disabled="isSaving || (onlyUnassigned ? !row.primary_instructor_id : !isRowDirty(row))"
                    @click="saveRow(row)"
                    :title="onlyUnassigned ? 
                      (!row.primary_instructor_id ? '講師を選択してください' : '') :
                      (!isRowDirty(row) ? '変更がありません' : '')"
                  >
                    {{ isSaving ? '保存中...' : 
                       onlyUnassigned ? '割当' :
                       hasAssignment(row) ? 
                         (row.primary_instructor_id ? '更新' : '割当解除') : 
                         '割当' }}
                  </button>
                  <button
                    v-if="row.primary_instructor_id"
                    class="btn btn-danger"
                    :disabled="isSaving"
                    @click="unassignOne(row)"
                  >
                    🗑 割当解除
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ページネーション -->
    <div class="pagination-section">
      <div class="pagination-info">
        <span>{{ totalCount }}件中 {{ ((page - 1) * pageSize) + 1 }}-{{ Math.min(page * pageSize, totalCount) }}件を表示</span>
      </div>
      
      <div class="pagination-controls">
        <button 
          class="btn btn-pagination" 
          :disabled="page <= 1" 
          @click="page--"
        >
          ← 前へ
        </button>
        
                 <div class="page-numbers">
                   <template v-for="(item, idx) in visiblePages" :key="`p-${idx}-${item}`">
                     <!-- 数字ページ -->
                     <button
                       v-if="typeof item === 'number'"
                       class="btn btn-page"
                       :class="{ 'active': item === page }"
                       @click="page = item"
                     >
                       {{ item }}
                     </button>
                     <!-- 省略記号はボタンではなく span -->
                     <span v-else class="page-ellipsis">…</span>
                   </template>
                 </div>
        
        <button 
          class="btn btn-pagination" 
          :disabled="page >= pageCount" 
          @click="page++"
        >
          次へ →
        </button>
      </div>
    </div>

    <!-- グループ作成フォーム -->
    <GroupCreator @created="loadMasters" />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { supabase } from '../../supabaseClient'
import GroupCreator from '../../components/admin/GroupCreator.vue'

// デバウンス用のユーティリティ
const debounce = (func, wait) => {
  let timeout
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout)
      func(...args)
    }
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}

// 状態管理
const isLoading = ref(false)
const isSaving = ref(false)
const rows = ref([])
const selectedInstructorId = ref('all') // 'all' or UUID
const selectedIds = ref([])
const allChecked = ref(false)
const instructors = ref([])
const groups = ref([])

// 講師ID→講師情報のマップ
const instructorMap = computed(() => {
  const map = {}
  for (const i of instructors.value) map[i.id] = i
  return map
})

// 全体表示用の割当マップ
const allAssignmentsMap = ref(new Map())

// 全割当マップをロードする関数
const loadAllAssignmentsMap = async () => {
  try {
    const { data, error } = await supabase
      .from('instructor_assignments')
      .select(`
        learner_user_id,
        primary_instructor_id,
        instructor:primary_instructor_id ( id, full_name )
      `)

    if (error) throw error

    const map = new Map()
    for (const r of (data || [])) {
      map.set(r.learner_user_id, {
        instructorId: r.primary_instructor_id,
        instructorName: r.instructor?.full_name ?? '(名称未設定)'
      })
    }
    allAssignmentsMap.value = map
  } catch (error) {
    console.error('全割当マップ読み込みエラー:', error)
  }
}

// 行の状態ヘルパー
const hasAssignment = (row) => {
  // 未割当ビューは常に未割当
  if (onlyUnassigned.value) return false
  // バッジ/dirty判定は_originalベース（安定）
  return !!row?._original?.primary_instructor_id
}

const isRowDirty = (row) => {
  // _originalが存在しない場合は変更なしとみなす
  if (!row._original) return false
  
  return row.primary_instructor_id !== row._original.primary_instructor_id ||
         row.group_id !== row._original.group_id ||
         (row.note || '') !== (row._original.note || '')
}

// ページネーション・フィルター
const page = ref(1)
const pageSize = ref(20)
const searchQuery = ref('')
const onlyUnassigned = ref(true)
const totalCount = ref(0)

// 計算プロパティ
const pageCount = computed(() => Math.ceil(totalCount.value / pageSize.value))

const visiblePages = computed(() => {
  const current = page.value
  const total = pageCount.value
  const delta = 2
  const range = []
  
  for (let i = Math.max(2, current - delta); i <= Math.min(total - 1, current + delta); i++) {
    range.push(i)
  }
  
  if (current - delta > 2) {
    range.unshift('...')
  }
  if (current + delta < total - 1) {
    range.push('...')
  }
  
  range.unshift(1)
  if (total > 1) {
    range.push(total)
  }
  
  return range
})

// マスタデータ読み込み
const loadMasters = async () => {
  try {
    // 講師データ取得
    const { data: instructorData, error: instructorError } = await supabase
      .from('users_phase2')
      .select('id, full_name, email, role')
      .eq('role', 'instructor')
      .order('full_name', { ascending: true })

    if (instructorError) throw instructorError
    instructors.value = (instructorData || []).map(r => ({
      id: r.id,
      full_name: r.full_name || '(名前未設定)',
      email: r.email || ''
    }))

    // グループデータ取得
    const { data: groupData, error: groupError } = await supabase
      .from('instructor_groups')
      .select('id, name, description')
      .order('name', { ascending: true })

    if (groupError) throw groupError
    groups.value = groupData || []

  } catch (error) {
    console.error('マスタデータ読み込みエラー:', error)
    alert('マスタデータの読み込みに失敗しました')
  }
}

// データ読み込み
const loadRows = async () => {
  isLoading.value = true
  try {
    let baseQuery

    if (onlyUnassigned.value) {
      // 未割当ビュー（カラム名変更）
      baseQuery = supabase
        .from('unassigned_learners')
        .select('learner_user_id, name, name_kana, birth_date', { count: 'exact' })
    } else if (selectedInstructorId.value !== 'all') {
      // 特定講師の担当生徒だけを引く（効率重視で assignments 起点）
      baseQuery = supabase
        .from('instructor_assignments')
        .select(`
          learner_user_id,
          primary_instructor_id,
          group_id,
          note,
          users_phase2:learner_user_id ( id, full_name ),
          instructor_groups:group_id ( id, name )
        `, { count: 'exact' })
        .eq('primary_instructor_id', selectedInstructorId.value)
    } else {
      // 全件表示：users_phase2（role='adult'）+ instructor_assignments
      // FK名を明示して多重リレーションエラーを回避
      baseQuery = supabase
        .from('users_phase2')
        .select(`
          id,
          full_name,
          role,
          instructor_assignments:instructor_assignments!instructor_assignments_learner_user_id_fkey(
            primary_instructor_id,
            group_id,
            note
          )
        `, { count: 'exact' })
        .eq('role', 'adult')
    }

    // 検索フィルター（2文字以上で有効）
    const q = searchQuery.value.trim()
    if (q.length >= 2) {
      if (onlyUnassigned.value) {
        baseQuery = baseQuery.or(`name.ilike.%${q}%`)
      } else {
        baseQuery = baseQuery.ilike('full_name', `%${q}%`)
      }
    }

    // ページネーション
    const from = (page.value - 1) * pageSize.value
    const to = from + pageSize.value - 1

    const { data, error, count } = await baseQuery
      .order(
        onlyUnassigned.value ? 'name' : 
        selectedInstructorId.value !== 'all' ? 'full_name' : 'full_name', 
        { 
          ascending: true,
          foreignTable: selectedInstructorId.value !== 'all' ? 'users_phase2' : undefined
        }
      )
      .range(from, to)

    if (error) throw error
    totalCount.value = count || 0

    // デバッグ用ログ
    console.log('loadRows - mode:', {
      onlyUnassigned: onlyUnassigned.value,
      selectedInstructorId: selectedInstructorId.value,
      dataCount: data?.length || 0,
      totalCount: count,
      queryType: onlyUnassigned.value ? 'unassigned_learners' : 
                selectedInstructorId.value !== 'all' ? 'instructor_assignments' : 'users_phase2'
    })
    
    // 実際のクエリ結果をログ出力
    if (data && data.length > 0) {
      console.log('loadRows - sample data:', data[0])
    } else {
      console.log('loadRows - no data returned')
    }

    // データ整形（ID/名前のフィールド名の差異に対応）
    if (onlyUnassigned.value) {
      rows.value = (data || []).map(r => ({
        learner_user_id: r.learner_user_id,
        display_name: r.name,            // ビューのname
        name_kana: r.name_kana || null,
        birth_date: r.birth_date || null,
        primary_instructor_id: null,
        group_id: null,
        note: '',
        // 必ずnullを入れる（マップで補完しない）
        _original: {
          primary_instructor_id: null,
          group_id: null,
          note: ''
        }
      }))
    } else if (selectedInstructorId.value !== 'all') {
      // assignments起点の形
      rows.value = (data || []).map(r => {
        const shaped = {
          learner_user_id: r.learner_user_id,
          display_name: r.users_phase2?.full_name ?? '(名前未設定)',
          name_kana: null,
          birth_date: null,
          primary_instructor_id: r.primary_instructor_id,
          group_id: r.group_id || null,
          note: r.note || ''
        }
        // _originalを必ずセット
        shaped._original = {
          primary_instructor_id: r.primary_instructor_id,
          group_id: r.group_id || null,
          note: r.note || ''
        }
        return shaped
      })
    } else {
      rows.value = (data || []).map(r => {
        const viaJoin = r.instructor_assignments?.[0]
        const viaMap = allAssignmentsMap.value.get(r.id) // 補完

        const currentInstructorId =
          viaJoin?.primary_instructor_id ?? viaMap?.instructorId ?? null

        const shaped = {
          learner_user_id: r.id,                // users_phase2.id
          display_name: r.full_name,       // users_phase2.full_name
          name_kana: null,                 // 使わないのでnull
          birth_date: null,                // 同上
          primary_instructor_id: currentInstructorId,       // 現在値
          group_id: viaJoin?.group_id ?? null,
          note: viaJoin?.note ?? ''
        }
        // バッジ/dirty用の元値
        shaped._original = {
          primary_instructor_id: currentInstructorId,
          group_id: viaJoin?.group_id ?? null,
          note: viaJoin?.note ?? ''
        }
        
        return shaped
      })
    }

    // デバッグ用ログ（整形後）
    console.log('loadRows - after mapping:', {
      rowsCount: rows.value.length,
      firstRow: rows.value[0] || null
    })

  } catch (error) {
    console.error('データ読み込みエラー:', error)
    alert('データの読み込みに失敗しました')
  } finally {
    isLoading.value = false
  }
}

// 行保存
const saveRow = async (row) => {
  // 上書き確認：既存担当あり→担当変更のケース
  const prev = row._original?.primary_instructor_id
  const next = row.primary_instructor_id
  if (prev && prev !== next) {
    const prevName = instructorMap.value[prev]?.full_name || '不明'
    const nextName = next ? (instructorMap.value[next]?.full_name || '未割当') : '未割当'
    const ok = confirm(`既存の担当「${prevName}」を「${nextName}」に変更します。よろしいですか？`)
    if (!ok) return
  }

  isSaving.value = true
  try {
    // 未割当にする場合はDELETE、そうでなければUPSERT
    if (!row.primary_instructor_id) {
      const { error } = await supabase
        .from('instructor_assignments')
        .delete()
        .eq('learner_user_id', row.learner_user_id)
      if (error) throw error
    } else {
      const payload = {
        learner_user_id: row.learner_user_id,
        primary_instructor_id: row.primary_instructor_id,
        group_id: row.group_id || null,
        note: row.note || null
      }
      const { error } = await supabase
        .from('instructor_assignments')
        .upsert(payload, { onConflict: 'learner_user_id' })
      if (error) throw error
    }

    // 未割当モードでは保存後に行を削除、それ以外は _original を更新してdirty解除
    if (onlyUnassigned.value) {
      rows.value = rows.value.filter(r => r.learner_user_id !== row.learner_user_id)
      totalCount.value = Math.max(0, totalCount.value - 1)
    } else {
      // 保存成功後に_originalを現在値で更新
      row._original = {
        primary_instructor_id: row.primary_instructor_id,
        group_id: row.group_id,
        note: row.note || ''
      }
    }

    // 保存成功後は全割当マップを再ロード
    await loadAllAssignmentsMap()
    
    alert('保存が完了しました')
  } catch (e) {
    console.error('保存エラー:', e)
    alert(`保存に失敗しました: ${e.message || e}`)
  } finally {
    isSaving.value = false
  }
}

// リセット＆リロード
const resetAndReload = () => {
  page.value = 1
  loadRows()
}

// デバウンス付きのリロード関数
const debouncedReload = debounce(() => resetAndReload(), 200)

// ライフサイクル
onMounted(async () => {
  await loadMasters()
  await loadAllAssignmentsMap()
  await loadRows()
})

// ウォッチャー
watch([onlyUnassigned, pageSize], () => resetAndReload())
watch(searchQuery, () => debouncedReload()) // 検索はデバウンス適用
watch(page, () => loadRows())
watch(selectedInstructorId, (newValue) => {
  // 講師を選んだら「未割当のみ」は自動でOFFに
  if (newValue !== 'all' && onlyUnassigned.value) {
    onlyUnassigned.value = false
  }
  resetAndReload()
})

watch([rows, page], () => { 
  allChecked.value = false
  selectedIds.value = [] 
})

const toggleAll = () => {
  if (allChecked.value) {
    selectedIds.value = rows.value.map(r => r.learner_user_id)
  } else {
    selectedIds.value = []
  }
}

const unassignOne = async (row) => {
  if (!confirm(`「${row.display_name || 'この生徒'}」の割当を解除しますか？`)) return
  
  isSaving.value = true
  try {
    const { error } = await supabase
      .from('instructor_assignments')
      .delete()
      .eq('learner_user_id', row.learner_user_id)

    if (error) throw error

    // 未割当ビューならそのまま残す／それ以外ならUIから消す
    if (onlyUnassigned.value) {
      // 未割当ビューでは元々表示対象なので何もしない
    } else {
      rows.value = rows.value.filter(r => r.learner_user_id !== row.learner_user_id)
      totalCount.value = Math.max(0, totalCount.value - 1)
    }

    alert('割当を解除しました')
  } catch (e) {
    console.error('割当解除エラー:', e)
    alert(`割当解除に失敗しました: ${e.message || e}`)
  } finally {
    isSaving.value = false
  }
}

const bulkUnassign = async () => {
  if (selectedInstructorId.value === 'all') {
    alert('講師を選択してください')
    return
  }
  if (!confirm(`選択 ${selectedIds.value.length} 件の割当を解除します。よろしいですか？`)) return
  
  isSaving.value = true
  try {
    // 担当講師が選択された行だけを安全に解除
    const { error } = await supabase
      .from('instructor_assignments')
      .delete()
      .in('learner_user_id', selectedIds.value)
      .eq('primary_instructor_id', selectedInstructorId.value)
    
    if (error) throw error

    // 解除後は未割当タブを自動表示に切替（運用動線を短く）
    onlyUnassigned.value = true
    selectedInstructorId.value = 'all'
    selectedIds.value = []
    allChecked.value = false
    await loadRows()
    alert('割当を解除しました。未割当一覧から再割当してください。')
  } catch (e) {
    console.error(e)
    alert(e.message || '割当解除に失敗しました')
  } finally {
    isSaving.value = false
  }
}
</script>

<style scoped>
.admin-assignments {
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
}

.header {
  margin-bottom: 2rem;
  text-align: center;
}

.header h1 {
  color: #1f2937;
  font-size: 2rem;
  margin-bottom: 0.5rem;
}

.subtitle {
  color: #6b7280;
  font-size: 1rem;
}

.filters-section {
  background: #f9fafb;
  padding: 1.5rem;
  border-radius: 8px;
  margin-bottom: 2rem;
}

.filter-row {
  display: flex;
  gap: 1rem;
  align-items: end;
  flex-wrap: wrap;
}

.search-input,
.page-size-selector {
  flex: 1;
  min-width: 200px;
}

.filter-toggle {
  flex-shrink: 0;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-weight: 500;
}

.stats-section {
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
}

.stat-card {
  flex: 1;
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  text-align: center;
}

.stat-number {
  font-size: 2rem;
  font-weight: bold;
  color: #3b82f6;
  margin-bottom: 0.5rem;
}

.stat-label {
  color: #6b7280;
  font-size: 0.9rem;
}

.table-section {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-bottom: 2rem;
}

.loading,
.no-data {
  padding: 3rem;
  text-align: center;
  color: #6b7280;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f4f6;
  border-top: 4px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.table-container {
  overflow-x: auto;
}

.assignments-table {
  width: 100%;
  border-collapse: collapse;
}

.assignments-table th {
  background: #f9fafb;
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: #374151;
  border-bottom: 2px solid #e5e7eb;
}

.assignments-table td {
  padding: 1rem;
  border-bottom: 1px solid #e5e7eb;
  vertical-align: top;
}

.table-row:hover {
  background: #f9fafb;
}

.col-index { width: 60px; }
.col-student { width: 200px; }
.col-instructor { width: 250px; }
.col-group { width: 150px; }
.col-note { width: 200px; }
.col-actions { width: 120px; }

.student-info {
  line-height: 1.4;
}

.student-name {
  font-weight: 600;
  color: #1f2937;
}

.student-kana {
  font-size: 0.8rem;
  color: #6b7280;
  margin-top: 0.25rem;
}

.student-id {
  font-size: 0.7rem;
  color: #9ca3af;
  margin-top: 0.25rem;
}

.form-input,
.form-select {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 0.9rem;
}

.form-input:focus,
.form-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.instructor-select.required {
  border-color: #ef4444;
}

.note-input {
  font-size: 0.8rem;
}

.btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-save {
  background: #10b981;
  color: white;
}

.btn-save:hover:not(:disabled) {
  background: #059669;
}

.pagination-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0;
}

.pagination-info {
  color: #6b7280;
  font-size: 0.9rem;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-pagination {
  background: #f3f4f6;
  color: #374151;
  padding: 0.5rem 1rem;
}

.btn-pagination:hover:not(:disabled) {
  background: #e5e7eb;
}

.page-numbers {
  display: flex;
  gap: 0.25rem;
}

.btn-page {
  background: #f3f4f6;
  color: #374151;
  padding: 0.5rem 0.75rem;
  min-width: 40px;
}

.btn-page:hover {
  background: #e5e7eb;
}

.btn-page.active {
  background: #3b82f6;
  color: white;
}

.page-ellipsis {
  padding: 0.5rem 0.75rem;
  min-width: 40px;
  text-align: center;
  color: #9ca3af;
  user-select: none;
}

/* 一括操作バー */
.bulk-bar {
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
}

.bulk-info {
  font-weight: 600;
  color: #92400e;
}

.btn-danger {
  background: #dc2626;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-weight: 500;
}

.btn-danger:hover:not(:disabled) {
  background: #b91c1c;
}

.btn-danger:disabled {
  background: #9ca3af;
  cursor: not-allowed;
}

/* 選択列のスタイル */
.col-select {
  width: 40px;
  text-align: center;
}

.col-select input[type="checkbox"] {
  transform: scale(1.2);
}

/* 無効時の見た目 */
.disabled-label {
  color: #9ca3af;
  cursor: not-allowed;
}

/* アクションボタンのスタイル */
.action-buttons {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.action-buttons .btn {
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
}

/* 割当済みの視覚差で誤上書きを防ぐ */
.student-info.row-assigned { 
  background: #f9fafb; 
  border-radius: 6px; 
  padding: .25rem .5rem; 
}

.assignment-badge { 
  display: inline-block; 
  margin-top: .25rem; 
  padding: .1rem .4rem; 
  border-radius: 9999px; 
  font-size: .75rem; 
}

.assignment-badge.assigned { 
  background: #e0f2fe; 
  color: #075985; 
  border: 1px solid #bae6fd; 
}

.assignment-badge.unassigned { 
  background: #fee2e2; 
  color: #991b1b; 
  border: 1px solid #fecaca; 
}

.btn-page.ellipsis:hover {
  background: transparent;
  transform: none;
}

/* レスポンシブ対応 */
@media (max-width: 768px) {
  .admin-assignments {
    padding: 1rem;
  }
  
  .filter-row {
    flex-direction: column;
    align-items: stretch;
  }
  
  .stats-section {
    flex-direction: column;
  }
  
  .pagination-section {
    flex-direction: column;
    gap: 1rem;
  }
  
  .pagination-controls {
    justify-content: center;
  }
}
</style>
